import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react';
import { Icon } from './Icon';

// App-wide toast notifications. A success toast confirms a completed action; an
// error toast surfaces a save/network failure. Field-validation errors stay
// inline at the field — toasts are about the OUTCOME of an operation, not bad
// input. One ToastProvider mounts in AppShell; any component calls useToast().

export type ToastKind = 'success' | 'error' | 'info';
export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

export type ToastAction =
  | { type: 'add'; id: number; kind: ToastKind; message: string }
  | { type: 'remove'; id: number }
  | { type: 'clear' };

const MAX_VISIBLE = 4;

// Pure reducer (unit-tested). De-dupes an identical kind+message already on
// screen so a double-submit doesn't stack twins, and caps the stack so a burst
// can't bury the app — oldest falls off first.
export function toastReducer(state: Toast[], action: ToastAction): Toast[] {
  switch (action.type) {
    case 'add': {
      if (state.some((x) => x.kind === action.kind && x.message === action.message)) return state;
      return [...state, { id: action.id, kind: action.kind, message: action.message }].slice(-MAX_VISIBLE);
    }
    case 'remove':
      return state.filter((x) => x.id !== action.id);
    case 'clear':
      return [];
    default:
      return state;
  }
}

export interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

// Success/info auto-dismiss quickly; errors linger so they aren't missed. All
// are click-to-dismiss regardless.
const TTL: Record<ToastKind, number> = { success: 4000, info: 4000, error: 7000 };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, dispatch] = useReducer(toastReducer, []);
  const idRef = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const remove = useCallback((id: number) => {
    dispatch({ type: 'remove', id });
    const tm = timers.current.get(id);
    if (tm) {
      clearTimeout(tm);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      const id = (idRef.current += 1);
      dispatch({ type: 'add', id, kind, message });
      const tm = setTimeout(() => remove(id), TTL[kind]);
      timers.current.set(id, tm);
    },
    [remove],
  );

  // Clear any pending timers if the provider unmounts.
  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const tm of map.values()) clearTimeout(tm);
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push('success', m),
      error: (m) => push('error', m),
      info: (m) => push('info', m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={remove} />
    </ToastContext.Provider>
  );
}

// No-op fallback when rendered outside a ToastProvider — so any component that
// fires toasts stays renderable in isolation (unit/SSR tests, Storybook-style
// mounts) with no setup. In the real app the provider is always mounted in
// AppShell, so toasts fire; nothing silently breaks.
const NOOP: ToastApi = { success: () => {}, error: () => {}, info: () => {} };

export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NOOP;
}

const ICON: Record<ToastKind, string> = { success: 'check', error: 'alert-triangle', info: 'alert-triangle' };

function ToastViewport({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  if (toasts.length === 0) return null;
  return (
    <div className="toast-viewport" role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <div key={t.id} className={'toast toast-' + t.kind} role={t.kind === 'error' ? 'alert' : 'status'}>
          <Icon name={ICON[t.kind]} size={16} />
          <span className="toast-msg">{t.message}</span>
          <button className="toast-close" onClick={() => onDismiss(t.id)} aria-label="Dismiss">
            <Icon name="close" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
